import React, { useState, useRef, useEffect } from 'react';
import { Send, PlusCircle, Bot, AlertCircle, Loader2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { ChatMessage } from '../components/ChatMessage';
import { supabase } from '../lib/supabase';

interface Message {
  sender: string;
  message: string;
  id: string;
  response_metadata?: string | null;
}

export const Chat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [conversationId, setConversationId] = useState(uuidv4());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startNewConversation = () => {
    setConversationId(uuidv4());
    setMessages([]);
    setError(null);
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);
    const currentMessage = inputMessage;
    setInputMessage('');

    // Add user message immediately
    const userMessage = {
      sender: 'human',
      message: currentMessage,
      id: uuidv4(),
      response_metadata: null
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      if (!navigator.onLine) {
        throw new Error('No hay conexión a internet. Por favor, verifica tu red e inténtalo nuevamente.');
      }

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/d948c0dd-372e-4228-87ba-64033faeb347', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          conversationId,
          sender: 'human',
          message: currentMessage,
          user_id: user.id
        }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => null);
        throw new Error(
          errorText || `Error del servidor: ${response.status} ${response.statusText}`
        );
      }

      let data;
      try {
        data = await response.json();
      } catch (e) {
        throw new Error('Respuesta inválida del servidor. Por favor, inténtalo nuevamente.');
      }
      
      if (data.status === 'success' && Array.isArray(data.history)) {
        // Sort messages by ID in ascending order
        const sortedMessages = [...data.history].sort((a, b) => 
          parseInt(a.id) - parseInt(b.id)
        );
        setMessages(sortedMessages);
      } else {
        throw new Error(data.message || 'Formato de respuesta inesperado del servidor');
      }
    } catch (error) {
      let errorMessage = 'Falló el envío del mensaje. Por favor, inténtalo nuevamente.';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error instanceof TypeError) {
        errorMessage = 'Error de red. Por favor, verifica tu conexión.';
      }
      
      setError(errorMessage);
      console.error('Error sending message:', { error, errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen max-h-screen bg-[#EFF0F5]">
      {/* Header */}
      <div className="flex justify-between items-center p-4 bg-white border-b">
        <h1 className="text-xl font-semibold">Agente de Datos</h1>
        <button
          onClick={startNewConversation}
          className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        >
          <PlusCircle className="w-5 h-5" />
          Nueva Conversación
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          {error && (
            <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 animate-fade-in">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}
          
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
              <Bot className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">Comienza una nueva conversación</p>
              <p className="text-sm text-center">¡Hola! Soy tu Agente de Datos de Inteligencia Artificial. Tengo acceso directo a la base de datos de tu negocio y estoy aquí para ayudarte con cualquier consulta que necesites.</p>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}
              {isLoading && (
                <div className="flex gap-4 p-6 bg-white animate-fade-in">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">IA</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                      <span className="text-gray-500">Escribiendo...</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="border-t bg-white p-4">
        <form onSubmit={sendMessage} className="max-w-4xl mx-auto">
          <div className="flex gap-4">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Escribe tu mensaje..."
              className="flex-1 p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !inputMessage.trim()}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-5 h-5" />
              Enviar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};